import { Input, InputGroup, InputLeftAddon } from '@chakra-ui/react';
import React from 'react';
import './CustomInput.scss';
import { Controller } from 'react-hook-form';

import CustomDescriptionText from '@/common/CustomDescriptionText/CustomDescriptionText';
import { CustomFormControl } from '@/common/CustomFormControl/CustomFormControl';
import CustomFormFieldLabel from '@/common/CustomFormFieldLabel/CustomFormFieldLabel';
import { sanitizeClientInput } from '@/utils/sanitizeClientInput';

interface CustomInputProps {
  name: string;
  label: string;
  placeholder: string;
  description?: string;
  maxLength?: number;
  autoFocus?: boolean;
  leftAddOn?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  showFormValueOnly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  type?: string;
  onKeyDown?: (event: any) => void;
}

const CustomInput = ({
  name,
  label,
  placeholder,
  description,
  maxLength = 150,
  autoFocus = false,
  leftAddOn,
  isRequired = false,
  isReadOnly = false,
  showFormValueOnly = false,
  size = 'md',
  isDisabled = false,
  type = 'text',
  onKeyDown,
}: CustomInputProps) => {
  const isEmail = type === 'email';
  const isText = type === 'text';
  const isNumber = type === 'number';

  const inputSize = size === 'sm' ? 'md' : size;

  return (
    <CustomFormControl
      componentName={'Custom Input'}
      name={name}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      showFormValueOnly={showFormValueOnly}
      size={size}
    >
      <CustomFormFieldLabel
        label={label}
        size={size}
        isRequired={isRequired}
        isDisabled={isDisabled}
      />

      {description && <CustomDescriptionText description={description} />}

      <Controller
        name={name}
        defaultValue={''}
        render={({ field }) => {
          const formatEmailAddress = (email: string) => {
            const formattedEmailInLowercaseAndWithoutSpaces = email
              ?.replace(/\s+/g, '') // remove all spaces
              .toLowerCase(); // convert email to lowercase
            return formattedEmailInLowercaseAndWithoutSpaces;
          };

          const emailOnBlur = () => {
            const formattedEmail = formatEmailAddress(field.value);
            field.onChange(formattedEmail);
            field.onBlur();
          };

          const textOnBlur = () => {
            const formattedString = field.value?.trim(); // remove whitespaces at both ends
            field.onChange(formattedString);
            field.onBlur();
          };

          const numberOnBlur = () => {
            const value = field.value?.toString().trim(); // remove whitespaces at both ends

            // If the field is empty, set it to an empty string instead of converting it to 0
            const formattedNumber = value === '' ? '' : Number(value);

            field.onChange(formattedNumber);
            field.onBlur();

            // Update the input to reflect the correctly formatted value
            field.value =
              formattedNumber === '' ? '' : formattedNumber.toString();
          };

          const actionOnBlur = () => {
            if (isEmail) emailOnBlur();
            if (isText) textOnBlur();
            if (isNumber) numberOnBlur();
            return field.onBlur();
          };

          const numberOnChange = (
            event: React.ChangeEvent<HTMLInputElement>
          ) => {
            const value = event.target.value;

            // If the value is empty, pass an empty string; otherwise, convert to a number
            const numericValue = value === '' ? '' : Number(value);

            field.onChange(numericValue);

            // Ensure the input value is updated to reflect this change
            event.target.value =
              numericValue === '' ? '' : numericValue.toString();
          };

          return (
            <InputGroup>
              {leftAddOn && <InputLeftAddon>{leftAddOn}</InputLeftAddon>}

              <Input
                {...field}
                placeholder={placeholder}
                maxLength={maxLength}
                autoFocus={autoFocus}
                required={isRequired}
                size={inputSize}
                disabled={isDisabled}
                onBlur={actionOnBlur}
                onChange={
                  isNumber
                    ? numberOnChange
                    : e => {
                        const clean = sanitizeClientInput(e.target.value);
                        field.onChange(clean);
                      }
                }
                onKeyDown={onKeyDown}
                type={type}
              />
            </InputGroup>
          );
        }}
      />
    </CustomFormControl>
  );
};

export default CustomInput;
